import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart3, TrendingUp, TrendingDown, GitBranch, Car, BookOpen, ShoppingBag,
  RefreshCw, DollarSign, Calendar, Search, ChevronLeft, ChevronRight,
  Plus, X, Copy, Check, Landmark, CheckCircle2, AlertCircle, Wallet, Ban
} from 'lucide-react';
import { reportesService, egresosService, conciliacionBancariaService, subserviciosService, categoriasSolicitudService, retencionesService, compromisosPagoService } from '../../../services/api';
import { formatearBolivares } from '../../../utils/formatters';
import useModal from '../../../hooks/useModal';

const TABS = [
  { id: 'rentabilidad', label: 'Rentabilidad', icon: TrendingUp, usaFecha: true },
  { id: 'egresos', label: 'Egresos', icon: TrendingDown, usaFecha: true },
  { id: 'retenciones', label: 'Retenciones', icon: Landmark, usaFecha: false },
  { id: 'flujo-caja', label: 'Flujo de Caja', icon: Wallet, usaFecha: false },
  { id: 'pipeline', label: 'Pipeline', icon: GitBranch, usaFecha: false },
  { id: 'vehiculos', label: 'Vehículos', icon: Car, usaFecha: false },
  { id: 'libro-ventas', label: 'Libro de Ventas', icon: BookOpen, usaFecha: true },
  { id: 'libro-compras', label: 'Libro de Compras', icon: ShoppingBag, usaFecha: true },
];

// Lineas de negocio reales del proyecto (CLAUDE.md); 'sin_linea' es el
// balde honesto para gasto que todavia no se etiqueto con un servicio.
const LINEAS_NEGOCIO = ['COT', 'MULTI', 'PUNTOV', 'DEV', 'IMP3D', 'GAMING', 'AUTO', 'GAMAN', 'sin_linea'];

const fmtMoneda = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtPct = (n) => (n === null || n === undefined ? '—' : `${(Number(n) * 100).toFixed(1)}%`);
const fmtFecha = (f) => (f ? new Date(f).toLocaleDateString('es-VE') : '—');

const ReportesView = () => {
  const [activeTab, setActiveTab] = useState('rentabilidad');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [facturadaFiltro, setFacturadaFiltro] = useState('');
  const [lineaNegocioFiltro, setLineaNegocioFiltro] = useState('');
  const [busquedaProveedor, setBusquedaProveedor] = useState('');
  const [paginaEgresos, setPaginaEgresos] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [rentabilidad, setRentabilidad] = useState({ cotizaciones: [], por_linea: [] });
  const [percepcion, setPercepcion] = useState(null);
  const [egresos, setEgresos] = useState({
    facturas: [], egresos_generales: [], egresos_generales_total: 0,
    egresos_generales_pagina: 1, egresos_generales_limite: 20,
    por_linea: [], total_negocio: []
  });
  const [pipeline, setPipeline] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const [libroVentas, setLibroVentas] = useState([]);
  const [libroCompras, setLibroCompras] = useState([]);

  const filtrosFecha = () => {
    const f = {};
    if (fechaInicio) f.fecha_inicio = fechaInicio;
    if (fechaFin) f.fecha_fin = fechaFin;
    return f;
  };

  const cargar = useCallback(async (tab, paginaOverride) => {
    setLoading(true);
    setError('');
    try {
      if (tab === 'rentabilidad') {
        const filtros = { ...filtrosFecha() };
        if (facturadaFiltro) filtros.facturada = facturadaFiltro;
        const [r, p] = await Promise.all([
          reportesService.getRentabilidad(filtros),
          reportesService.getPercepcion(filtrosFecha())
        ]);
        setRentabilidad(r.data || { cotizaciones: [], por_linea: [] });
        setPercepcion(p.data || null);
      } else if (tab === 'egresos') {
        const filtros = { ...filtrosFecha() };
        if (lineaNegocioFiltro) filtros.linea_negocio = lineaNegocioFiltro;
        if (busquedaProveedor) filtros.busqueda = busquedaProveedor;
        filtros.pagina = paginaOverride || 1;
        const res = await reportesService.getEgresos(filtros);
        setEgresos(res.data || {
          facturas: [], egresos_generales: [], egresos_generales_total: 0,
          egresos_generales_pagina: 1, egresos_generales_limite: 20,
          por_linea: [], total_negocio: []
        });
        setPaginaEgresos(filtros.pagina);
      } else if (tab === 'pipeline') {
        const res = await reportesService.getPipeline();
        setPipeline(res.data || []);
      } else if (tab === 'vehiculos') {
        const res = await reportesService.getRentabilidadVehiculos();
        setVehiculos(res.data || []);
      } else if (tab === 'libro-ventas') {
        const res = await reportesService.getLibroVentas(filtrosFecha());
        setLibroVentas(res.data || []);
      } else if (tab === 'libro-compras') {
        const res = await reportesService.getLibroCompras(filtrosFecha());
        setLibroCompras(res.data || []);
      }
    } catch (err) {
      setError(err.message || 'Error al cargar el reporte');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fechaInicio, fechaFin, facturadaFiltro, lineaNegocioFiltro, busquedaProveedor]);

  useEffect(() => {
    cargar(activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const tabActual = TABS.find(t => t.id === activeTab);

  return (
    <div className="p-2 md:p-6 space-y-6">
      <div>
        <h2 className="text-xl lg:text-2xl font-bold text-gray-800 flex items-center gap-2">
          <BarChart3 className="text-blue-600" />
          Reportes
        </h2>
        <p className="text-sm lg:text-base text-gray-600">
          Rentabilidad, pipeline de solicitudes y libros fiscales.
        </p>
      </div>

      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-6 overflow-x-auto" aria-label="Tabs">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon size={18} />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {tabActual?.usaFecha && (
        <div className="flex flex-wrap items-end gap-3 bg-gray-50 border border-gray-200 rounded-xl p-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
              <Calendar size={12} /> Desde
            </label>
            <input
              type="date"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Hasta</label>
            <input
              type="date"
              value={fechaFin}
              onChange={(e) => setFechaFin(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            />
          </div>
          {activeTab === 'rentabilidad' && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Facturación</label>
              <select
                value={facturadaFiltro}
                onChange={(e) => setFacturadaFiltro(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Facturadas y no facturadas</option>
                <option value="true">Solo facturadas</option>
                <option value="false">Solo no facturadas</option>
              </select>
            </div>
          )}
          {activeTab === 'egresos' && (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Línea de negocio</label>
                <select
                  value={lineaNegocioFiltro}
                  onChange={(e) => setLineaNegocioFiltro(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Todas</option>
                  {LINEAS_NEGOCIO.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Buscar por proveedor</label>
                <div className="relative">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={busquedaProveedor}
                    onChange={(e) => setBusquedaProveedor(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') cargar('egresos'); }}
                    placeholder="Ej: Pepsico, Cocacola..."
                    className="pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 w-48"
                  />
                </div>
              </div>
            </>
          )}
          <button
            onClick={() => cargar(activeTab)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm font-medium"
          >
            <RefreshCw size={16} /> Aplicar
          </button>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">{error}</div>
      )}

      {loading ? (
        <div className="text-center py-12">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      ) : (
        <>
          {activeTab === 'rentabilidad' && (
            <RentabilidadTab rentabilidad={rentabilidad} percepcion={percepcion} />
          )}
          {activeTab === 'egresos' && (
            <EgresosTab egresos={egresos} onCambiarPagina={(p) => cargar('egresos', p)} />
          )}
          {activeTab === 'retenciones' && <RetencionesTab />}
          {activeTab === 'flujo-caja' && <FlujoCajaTab />}
          {activeTab === 'pipeline' && <PipelineTab pipeline={pipeline} />}
          {activeTab === 'vehiculos' && <VehiculosTab vehiculos={vehiculos} />}
          {activeTab === 'libro-ventas' && <LibroVentasTab filas={libroVentas} />}
          {activeTab === 'libro-compras' && <LibroComprasTab filas={libroCompras} />}
        </>
      )}
    </div>
  );
};

const KpiCard = ({ icon: Icon, label, value, iconClass = 'text-blue-600' }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
    <div className="flex items-center gap-2 text-gray-500 text-xs font-medium uppercase tracking-wide mb-1">
      <Icon size={14} className={iconClass} /> {label}
    </div>
    <p className="text-xl font-bold text-gray-800">{value}</p>
  </div>
);

const FacturadaBadge = ({ facturada, estado }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
    facturada ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
  }`} title={estado ? `Factura ${estado}` : ''}>
    {facturada ? 'Facturada' : 'Sin factura'}
  </span>
);

const RentabilidadTab = ({ rentabilidad, percepcion }) => {
  const { cotizaciones = [], por_linea = [] } = rentabilidad;

  // Las tarjetas KPI suman un solo numero: solo se acumulan filas en USD
  // (la moneda dominante hoy) para no mezclar USD con Bs en un mismo total.
  const totales = por_linea
    .filter(l => l.moneda === 'USD')
    .reduce((acc, l) => ({
      venta: acc.venta + Number(l.venta || 0),
      ventaFacturada: acc.ventaFacturada + (l.facturada ? Number(l.venta || 0) : 0),
      ventaSinFacturar: acc.ventaSinFacturar + (!l.facturada ? Number(l.venta || 0) : 0),
      ingresoFacturado: acc.ingresoFacturado + Number(l.ingreso_facturado || 0),
      ingresoCobrado: acc.ingresoCobrado + Number(l.ingreso_cobrado || 0),
      ganancia: acc.ganancia + Number(l.ganancia || 0)
    }), { venta: 0, ventaFacturada: 0, ventaSinFacturar: 0, ingresoFacturado: 0, ingresoCobrado: 0, ganancia: 0 });
  const ingresoPendiente = Math.max(0, totales.ingresoFacturado - totales.ingresoCobrado);
  const margenGlobal = totales.venta !== 0 ? totales.ganancia / totales.venta : null;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <KpiCard icon={DollarSign} label="Ingreso facturado (USD)" value={fmtMoneda(totales.ingresoFacturado)} iconClass="text-green-600" />
        <KpiCard icon={DollarSign} label="Ingreso cobrado (USD)" value={fmtMoneda(totales.ingresoCobrado)} iconClass="text-emerald-600" />
        <KpiCard icon={DollarSign} label="Pendiente de cobro (USD)" value={fmtMoneda(ingresoPendiente)} iconClass="text-orange-600" />
        <KpiCard icon={DollarSign} label="Ingreso sin facturar (USD)" value={fmtMoneda(totales.ventaSinFacturar)} iconClass="text-amber-600" />
        <KpiCard icon={TrendingUp} label="Ganancia acumulada (USD)" value={fmtMoneda(totales.ganancia)} iconClass="text-blue-600" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <KpiCard icon={BarChart3} label="Margen global (USD)" value={fmtPct(margenGlobal)} iconClass="text-purple-600" />
        <KpiCard icon={DollarSign} label="Venta proyectada (USD)" value={fmtMoneda(totales.venta)} iconClass="text-slate-600" />
        <KpiCard icon={DollarSign} label="Venta sin factura (USD)" value={fmtMoneda(totales.ventaSinFacturar)} iconClass="text-amber-600" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <KpiCard icon={DollarSign} label="Percibido USD (pagado)" value={fmtMoneda(percepcion?.total_usd)} iconClass="text-green-600" />
        <KpiCard icon={DollarSign} label="Percibido Bs (pagado)" value={`Bs. ${Number(percepcion?.total_ves || 0).toFixed(2)}`} iconClass="text-emerald-600" />
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
          <h3 className="font-semibold text-gray-700 text-sm">Por línea de negocio y estado de facturación</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-left text-xs text-gray-500 uppercase">
                <th className="px-4 py-2">Línea</th>
                <th className="px-4 py-2">Facturación</th>
                <th className="px-4 py-2">Moneda</th>
                <th className="px-4 py-2 text-center">Cotizaciones</th>
                <th className="px-4 py-2 text-right">Costo</th>
                <th className="px-4 py-2 text-right">Venta</th>
                <th className="px-4 py-2 text-right">Facturado</th>
                <th className="px-4 py-2 text-right">Cobrado</th>
                <th className="px-4 py-2 text-right">Pendiente</th>
                <th className="px-4 py-2 text-right">Ganancia</th>
                <th className="px-4 py-2 text-right">Margen</th>
              </tr>
            </thead>
            <tbody>
              {por_linea.length === 0 ? (
                <tr><td colSpan={11} className="px-4 py-6 text-center text-gray-400">Sin datos en el rango seleccionado</td></tr>
              ) : por_linea.map((l) => (
                <tr key={`${l.linea_negocio}-${l.facturada}-${l.moneda}`} className="border-b border-gray-100">
                  <td className="px-4 py-2 font-medium text-gray-800">{l.linea_negocio}</td>
                  <td className="px-4 py-2"><FacturadaBadge facturada={l.facturada} /></td>
                  <td className="px-4 py-2 text-gray-500">{l.moneda}</td>
                  <td className="px-4 py-2 text-center">{l.cotizaciones}</td>
                  <td className="px-4 py-2 text-right">{fmtMoneda(l.costo)}</td>
                  <td className="px-4 py-2 text-right">{fmtMoneda(l.venta)}</td>
                  <td className="px-4 py-2 text-right text-green-700">{fmtMoneda(l.ingreso_facturado)}</td>
                  <td className="px-4 py-2 text-right text-emerald-700">{fmtMoneda(l.ingreso_cobrado)}</td>
                  <td className="px-4 py-2 text-right text-orange-700">{fmtMoneda(Number(l.ingreso_facturado || 0) - Number(l.ingreso_cobrado || 0))}</td>
                  <td className="px-4 py-2 text-right font-semibold text-green-700">{fmtMoneda(l.ganancia)}</td>
                  <td className="px-4 py-2 text-right">{fmtPct(l.margen)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
          <h3 className="font-semibold text-gray-700 text-sm">Por cotización ({cotizaciones.length})</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-left text-xs text-gray-500 uppercase">
                <th className="px-4 py-2">Número</th>
                <th className="px-4 py-2">Fecha</th>
                <th className="px-4 py-2">Línea</th>
                <th className="px-4 py-2">Contraparte</th>
                <th className="px-4 py-2 text-right">Costo</th>
                <th className="px-4 py-2 text-right">Venta</th>
                <th className="px-4 py-2 text-right">Facturado</th>
                <th className="px-4 py-2 text-right">Cobrado</th>
                <th className="px-4 py-2 text-right">Pendiente</th>
                <th className="px-4 py-2 text-right">Margen</th>
                <th className="px-4 py-2">Estado</th>
                <th className="px-4 py-2">Facturación</th>
              </tr>
            </thead>
            <tbody>
              {cotizaciones.length === 0 ? (
                <tr><td colSpan={12} className="px-4 py-6 text-center text-gray-400">Sin cotizaciones en el rango seleccionado</td></tr>
              ) : cotizaciones.map((c) => (
                <tr key={c.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-2 font-medium text-gray-800">{c.numero}</td>
                  <td className="px-4 py-2 text-gray-500">{fmtFecha(c.fecha)}</td>
                  <td className="px-4 py-2">{c.linea_negocio}</td>
                  <td className="px-4 py-2">{c.contraparte}</td>
                  <td className="px-4 py-2 text-right">{fmtMoneda(c.costo_total)}</td>
                  <td className="px-4 py-2 text-right">{fmtMoneda(c.venta_total)}</td>
                  <td className="px-4 py-2 text-right text-green-700">{fmtMoneda(c.ingreso_facturado)}</td>
                  <td className="px-4 py-2 text-right text-emerald-700">{fmtMoneda(c.ingreso_cobrado)}</td>
                  <td className="px-4 py-2 text-right text-orange-700">{fmtMoneda(Number(c.ingreso_facturado || 0) - Number(c.ingreso_cobrado || 0))}</td>
                  <td className="px-4 py-2 text-right">{fmtPct(c.margen)}</td>
                  <td className="px-4 py-2 text-gray-500">{c.estado}</td>
                  <td className="px-4 py-2">
                    <FacturadaBadge facturada={c.facturada} estado={c.factura_estado} />
                    {c.factura_numero && <span className="block text-[11px] text-gray-400 mt-0.5">{c.factura_numero}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const ESTADO_COMPRA_LABEL = {
  registrada: 'Registrada', retenida: 'Retenida', pagada: 'Pagada', anulada: 'Anulada'
};

const BotonCopiarConcepto = ({ texto }) => {
  const [copiado, setCopiado] = useState(false);
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      // clipboard no disponible (permiso/contexto no seguro) -- el usuario copia a mano
    }
  };
  return (
    <button
      onClick={copiar}
      className="flex items-center gap-1 px-2 py-1 bg-emerald-600 text-white rounded text-xs font-medium hover:bg-emerald-700 shrink-0"
    >
      {copiado ? <Check size={12} /> : <Copy size={12} />} {copiado ? 'Copiado' : 'Copiar'}
    </button>
  );
};

const NuevoEgresoModal = ({ onClose, onCreado }) => {
  const [form, setForm] = useState({
    fecha: new Date().toISOString().slice(0, 10),
    descripcion: '', linea_negocio: '', subservicio_id: '', categoria_id: '', moneda: 'USD', monto: '', cuenta_origen: ''
  });
  const [cuentas, setCuentas] = useState([]);
  const [subservicios, setSubservicios] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [creado, setCreado] = useState(null);

  useEffect(() => {
    conciliacionBancariaService.getCuentas().then(res => setCuentas(res.data || [])).catch(() => {});
    categoriasSolicitudService.getAll({ activo: true }).then(res => setCategorias(res.data || [])).catch(() => {});
  }, []);

  // Un subservicio pertenece a una sola linea de negocio -- se filtra en
  // cascada, y si la linea cambia el subservicio elegido ya no es valido.
  useEffect(() => {
    if (!form.linea_negocio) { setSubservicios([]); return; }
    subserviciosService.getAll({ activo: true, linea_negocio: form.linea_negocio })
      .then(res => setSubservicios(res.data || []))
      .catch(() => setSubservicios([]));
    setForm(f => ({ ...f, subservicio_id: '' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.linea_negocio]);

  const handleGuardar = async () => {
    setError('');
    setGuardando(true);
    try {
      const res = await egresosService.crear(form);
      setCreado(res.data);
      onCreado();
    } catch (err) {
      setError(err.message || 'Error al crear el egreso');
    } finally {
      setGuardando(false);
    }
  };

  const concepto = creado ? `${creado.codigo} - ${creado.descripcion}` : '';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white">
          <h3 className="text-lg font-bold">Nuevo egreso</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="p-4 space-y-3">
          {creado ? (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                Egreso registrado como <span className="font-semibold">programado</span>. Usa este concepto en la transferencia real — cuando importes el extracto bancario, la conciliación lo reconocerá por este código:
              </p>
              <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg p-3">
                <code className="flex-1 text-sm text-gray-800 break-all">{concepto}</code>
                <BotonCopiarConcepto texto={concepto} />
              </div>
              <div className="flex justify-end">
                <button onClick={onClose} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">Cerrar</button>
              </div>
            </div>
          ) : (
            <>
              {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-2 text-sm">{error}</div>}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Fecha</label>
                <input
                  type="date"
                  value={form.fecha}
                  onChange={e => setForm({ ...form, fecha: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Descripción</label>
                <input
                  type="text"
                  value={form.descripcion}
                  onChange={e => setForm({ ...form, descripcion: e.target.value })}
                  placeholder="Ej: Pago a proveedor XYZ"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Línea de negocio</label>
                  <select
                    value={form.linea_negocio}
                    onChange={e => setForm({ ...form, linea_negocio: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="">Sin línea</option>
                    {LINEAS_NEGOCIO.filter(l => l !== 'sin_linea').map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Subservicio</label>
                  <select
                    value={form.subservicio_id}
                    onChange={e => setForm({ ...form, subservicio_id: e.target.value })}
                    disabled={!form.linea_negocio}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-400"
                  >
                    <option value="">{form.linea_negocio ? 'Sin subservicio' : 'Elige la línea primero'}</option>
                    {subservicios.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Categoría</label>
                <select
                  value={form.categoria_id}
                  onChange={e => setForm({ ...form, categoria_id: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">Sin categoría</option>
                  {categorias.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Moneda</label>
                  <select
                    value={form.moneda}
                    onChange={e => setForm({ ...form, moneda: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="USD">USD</option>
                    <option value="VES">VES (Bs)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Monto</label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.monto}
                    onChange={e => setForm({ ...form, monto: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Cuenta de origen</label>
                <select
                  value={form.cuenta_origen}
                  onChange={e => setForm({ ...form, cuenta_origen: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">Sin especificar</option>
                  {cuentas.map(c => <option key={c.id} value={c.alias}>{c.alias}</option>)}
                </select>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
                <button
                  onClick={handleGuardar}
                  disabled={guardando || !form.fecha || !form.descripcion || !form.monto}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-300 text-sm font-medium"
                >
                  {guardando ? 'Guardando...' : 'Registrar egreso'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const EgresosTab = ({ egresos, onCambiarPagina }) => {
  const {
    facturas = [], egresos_generales = [],
    egresos_generales_total = 0, egresos_generales_pagina = 1, egresos_generales_limite = 20,
    por_linea = [], total_negocio = []
  } = egresos;
  const totalPaginas = Math.max(1, Math.ceil(egresos_generales_total / egresos_generales_limite));
  const desde = egresos_generales.length === 0 ? 0 : (egresos_generales_pagina - 1) * egresos_generales_limite + 1;
  const hasta = (egresos_generales_pagina - 1) * egresos_generales_limite + egresos_generales.length;

  const [mostrarModal, setMostrarModal] = useState(false);
  const [programados, setProgramados] = useState([]);

  const cargarProgramados = useCallback(() => {
    egresosService.getAll({ estado: 'programado' }).then(res => setProgramados(res.data || [])).catch(() => {});
  }, []);

  useEffect(() => { cargarProgramados(); }, [cargarProgramados]);

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <button
          onClick={() => setMostrarModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium"
        >
          <Plus size={16} /> Nuevo egreso
        </button>
      </div>

      {mostrarModal && (
        <NuevoEgresoModal
          onClose={() => { setMostrarModal(false); cargarProgramados(); }}
          onCreado={cargarProgramados}
        />
      )}

      {programados.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
            <h3 className="font-semibold text-gray-700 text-sm">Egresos programados, pendientes de pago ({programados.length})</h3>
            <p className="text-xs text-gray-500">Todavía no salieron del banco. Copia el concepto y pégalo en la transferencia — la conciliación bancaria los reconocerá por su código.</p>
          </div>
          <div className="divide-y divide-gray-100">
            {programados.map(p => (
              <div key={p.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
                <div className="min-w-0">
                  <p className="text-sm text-gray-800 truncate">
                    <span className="font-mono text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded mr-2">{p.codigo}</span>
                    {p.descripcion}
                  </p>
                  <p className="text-xs text-gray-500">
                    {fmtFecha(p.fecha)} · {p.linea_negocio || 'sin línea'}
                    {p.subservicio_nombre && ` · ${p.subservicio_nombre}`}
                    {p.categoria_mostrar && ` · ${p.categoria_mostrar}`} ·{' '}
                    {p.monto_usd !== null ? fmtMoneda(p.monto_usd) : `Bs. ${Number(p.monto_bs || 0).toFixed(2)}`}
                  </p>
                </div>
                <BotonCopiarConcepto texto={`${p.codigo} - ${p.descripcion}`} />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {total_negocio.length === 0 ? (
          <KpiCard icon={TrendingDown} label="Total del negocio" value={fmtMoneda(0)} iconClass="text-red-600" />
        ) : total_negocio.map(t => (
          <KpiCard
            key={t.moneda}
            icon={TrendingDown}
            label={`Total del negocio (${t.moneda})`}
            value={t.moneda === 'VES' ? `Bs. ${Number(t.total || 0).toFixed(2)}` : fmtMoneda(t.total)}
            iconClass="text-red-600"
          />
        ))}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
          <h3 className="font-semibold text-gray-700 text-sm">Gasto por línea de negocio</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-left text-xs text-gray-500 uppercase">
                <th className="px-4 py-2">Línea</th>
                <th className="px-4 py-2">Moneda</th>
                <th className="px-4 py-2 text-center">Movimientos</th>
                <th className="px-4 py-2 text-right">Total gastado</th>
              </tr>
            </thead>
            <tbody>
              {por_linea.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-400">Sin gasto en el rango seleccionado</td></tr>
              ) : por_linea.map((l) => (
                <tr key={`${l.linea_negocio}-${l.moneda}`} className="border-b border-gray-100">
                  <td className="px-4 py-2 font-medium text-gray-800">{l.linea_negocio}</td>
                  <td className="px-4 py-2 text-gray-500">{l.moneda}</td>
                  <td className="px-4 py-2 text-center">{l.movimientos}</td>
                  <td className="px-4 py-2 text-right font-semibold text-red-700">
                    {l.moneda === 'VES' ? `Bs. ${Number(l.total || 0).toFixed(2)}` : fmtMoneda(l.total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
          <h3 className="font-semibold text-gray-700 text-sm">Facturas de compra ({facturas.length})</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-left text-xs text-gray-500 uppercase">
                <th className="px-4 py-2">N° Factura</th>
                <th className="px-4 py-2">Fecha</th>
                <th className="px-4 py-2">Proveedor</th>
                <th className="px-4 py-2">Línea</th>
                <th className="px-4 py-2 text-right">Total</th>
                <th className="px-4 py-2">Estado</th>
              </tr>
            </thead>
            <tbody>
              {facturas.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">Sin facturas de compra en el rango seleccionado</td></tr>
              ) : facturas.map((f) => (
                <tr key={f.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-2 font-medium text-gray-800">{f.numero_proveedor}</td>
                  <td className="px-4 py-2 text-gray-500">{fmtFecha(f.fecha)}</td>
                  <td className="px-4 py-2">{f.proveedor_nombre}</td>
                  <td className="px-4 py-2">{f.linea_negocio}</td>
                  <td className="px-4 py-2 text-right">{f.moneda === 'VES' ? `Bs. ${Number(f.total || 0).toFixed(2)}` : fmtMoneda(f.total)}</td>
                  <td className="px-4 py-2 text-gray-500">{ESTADO_COMPRA_LABEL[f.estado] || f.estado}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
          <h3 className="font-semibold text-gray-700 text-sm">Egresos generales ({egresos_generales_total})</h3>
          <p className="text-xs text-gray-500">Nómina, viáticos, impuestos, compra de divisas, reintegros y compras sin factura fiscal — sin desglose de IVA/retención. Más recientes primero.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-left text-xs text-gray-500 uppercase">
                <th className="px-4 py-2">Fecha</th>
                <th className="px-4 py-2">Descripción</th>
                <th className="px-4 py-2">Línea</th>
                <th className="px-4 py-2">Subservicio</th>
                <th className="px-4 py-2">Categoría</th>
                <th className="px-4 py-2 text-right">Monto USD</th>
                <th className="px-4 py-2 text-right">Monto Bs</th>
              </tr>
            </thead>
            <tbody>
              {egresos_generales.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-400">Sin egresos generales en el rango seleccionado</td></tr>
              ) : egresos_generales.map((e) => (
                <tr key={e.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-2 text-gray-500">{fmtFecha(e.fecha)}</td>
                  <td className="px-4 py-2 max-w-md truncate" title={e.descripcion}>{e.descripcion}</td>
                  <td className="px-4 py-2">{e.linea_negocio}</td>
                  <td className="px-4 py-2 text-gray-500">{e.subservicio || '—'}</td>
                  <td className="px-4 py-2 text-gray-500">{e.categoria || '—'}</td>
                  <td className="px-4 py-2 text-right">{e.monto_usd !== null ? fmtMoneda(e.monto_usd) : '—'}</td>
                  <td className="px-4 py-2 text-right">{e.monto_bs !== null ? `Bs. ${Number(e.monto_bs).toFixed(2)}` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {egresos_generales_total > 0 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between text-sm text-gray-500">
            <span>Mostrando {desde}–{hasta} de {egresos_generales_total}</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => onCambiarPagina(egresos_generales_pagina - 1)}
                disabled={egresos_generales_pagina <= 1}
                className="p-1.5 rounded-lg border border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="text-xs">Página {egresos_generales_pagina} de {totalPaginas}</span>
              <button
                onClick={() => onCambiarPagina(egresos_generales_pagina + 1)}
                disabled={egresos_generales_pagina >= totalPaginas}
                className="p-1.5 rounded-lg border border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
// La quincena solo es un periodo real de declaracion para IVA -- ISLR se
// declara mensual, aunque la columna quincena exista en todas las filas.
const fmtPeriodo = (periodo, quincena, impuesto) => {
  if (!periodo || periodo.length !== 6) return periodo;
  const mes = MESES[parseInt(periodo.slice(4, 6), 10) - 1] || periodo.slice(4, 6);
  const año = periodo.slice(0, 4);
  return (quincena && impuesto === 'iva') ? `${mes} ${año} · ${quincena}ª quincena` : `${mes} ${año}`;
};
const fmtMontoMoneda = (n, moneda) => (moneda === 'VES' ? `Bs. ${Number(n || 0).toFixed(2)}` : `$${Number(n || 0).toFixed(2)}`);

const RetencionesTab = () => {
  const { notify, ModalRenderer } = useModal();
  const [resumen, setResumen] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [grupoAbierto, setGrupoAbierto] = useState(null);
  const [detalle, setDetalle] = useState([]);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [accionEnCurso, setAccionEnCurso] = useState(null);

  const cargarResumen = async () => {
    setCargando(true);
    setError('');
    try {
      const res = await retencionesService.getResumenPendientes();
      setResumen(res.data || []);
    } catch (err) {
      setError(err.message || 'Error al cargar el resumen de retenciones');
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargarResumen(); }, []);

  const claveGrupo = (g) => `${g.periodo}|${g.quincena || ''}|${g.impuesto}|${g.moneda}`;

  const toggleGrupo = async (g) => {
    const clave = claveGrupo(g);
    if (grupoAbierto === clave) { setGrupoAbierto(null); return; }
    setGrupoAbierto(clave);
    setCargandoDetalle(true);
    try {
      const params = { direccion: 'emitida', periodo: g.periodo, impuesto: g.impuesto };
      const res = await retencionesService.getAll(params);
      setDetalle((res.data || []).filter(r => r.moneda === g.moneda && (g.quincena ? r.quincena === g.quincena : true) && r.estado !== 'anulada'));
    } catch (err) {
      await notify('error', 'Error', err.message || 'Error al cargar el detalle');
    } finally {
      setCargandoDetalle(false);
    }
  };

  const handleDeclarar = async (id) => {
    setAccionEnCurso(id);
    try {
      await retencionesService.cambiarEstado(id, 'declarada');
      await cargarResumen();
      setDetalle(prev => prev.map(r => r.id === id ? { ...r, estado: 'declarada' } : r));
    } catch (err) {
      await notify('error', 'Error', err.message || 'Error al declarar');
    } finally {
      setAccionEnCurso(null);
    }
  };

  const handleMarcarPagada = async (id) => {
    setAccionEnCurso(id);
    try {
      await retencionesService.marcarPagada(id);
      await cargarResumen();
      setDetalle(prev => prev.map(r => r.id === id ? { ...r, pagado_at: new Date().toISOString() } : r));
    } catch (err) {
      await notify('error', 'Error', err.message || 'Error al marcar como pagada');
    } finally {
      setAccionEnCurso(null);
    }
  };

  // Totales de tarjetas KPI: nunca se mezclan monedas distintas en una
  // misma suma.
  const totalesPorMoneda = {};
  for (const g of resumen) {
    if (!totalesPorMoneda[g.moneda]) totalesPorMoneda[g.moneda] = { pendiente_declarar: 0, pendiente_pagar: 0, ya_pagado: 0 };
    totalesPorMoneda[g.moneda].pendiente_declarar += Number(g.pendiente_declarar || 0);
    totalesPorMoneda[g.moneda].pendiente_pagar += Number(g.pendiente_pagar || 0);
    totalesPorMoneda[g.moneda].ya_pagado += Number(g.ya_pagado || 0);
  }

  if (cargando) return <div className="text-center py-12 text-gray-400">Cargando...</div>;

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-500">
        Solo retenciones <strong>emitidas</strong> (lo que Gaman retuvo a sus proveedores) — es la obligación real ante el SENIAT.
        Las retenciones recibidas son crédito fiscal a favor, no un compromiso de pago.
      </p>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">{error}</div>}

      {Object.entries(totalesPorMoneda).map(([moneda, t]) => (
        <div key={moneda} className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <KpiCard icon={AlertCircle} label={`Pendiente de declarar (${moneda})`} value={fmtMontoMoneda(t.pendiente_declarar, moneda)} iconClass="text-amber-600" />
          <KpiCard icon={Landmark} label={`Declarado, falta pagar (${moneda})`} value={fmtMontoMoneda(t.pendiente_pagar, moneda)} iconClass="text-orange-600" />
          <KpiCard icon={CheckCircle2} label={`Ya pagado (${moneda})`} value={fmtMontoMoneda(t.ya_pagado, moneda)} iconClass="text-emerald-600" />
        </div>
      ))}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
          <h3 className="font-semibold text-gray-700 text-sm">Retenciones emitidas por período</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-left text-xs text-gray-500 uppercase">
                <th className="px-4 py-2">Período</th>
                <th className="px-4 py-2">Impuesto</th>
                <th className="px-4 py-2 text-center">Cant.</th>
                <th className="px-4 py-2 text-right">Pendiente declarar</th>
                <th className="px-4 py-2 text-right">Declarado, falta pagar</th>
                <th className="px-4 py-2 text-right">Ya pagado</th>
              </tr>
            </thead>
            <tbody>
              {resumen.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">Sin retenciones emitidas registradas</td></tr>
              ) : resumen.map((g) => {
                const clave = claveGrupo(g);
                const abierto = grupoAbierto === clave;
                return (
                  <React.Fragment key={clave}>
                    <tr className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => toggleGrupo(g)}>
                      <td className="px-4 py-2 font-medium text-gray-800">{fmtPeriodo(g.periodo, g.quincena, g.impuesto)}</td>
                      <td className="px-4 py-2 uppercase text-gray-600">{g.impuesto}</td>
                      <td className="px-4 py-2 text-center">{g.cantidad}</td>
                      <td className="px-4 py-2 text-right text-amber-700 font-medium">{g.pendiente_declarar ? fmtMontoMoneda(g.pendiente_declarar, g.moneda) : '—'}</td>
                      <td className="px-4 py-2 text-right text-orange-700 font-medium">{g.pendiente_pagar ? fmtMontoMoneda(g.pendiente_pagar, g.moneda) : '—'}</td>
                      <td className="px-4 py-2 text-right text-emerald-700 font-medium">{g.ya_pagado ? fmtMontoMoneda(g.ya_pagado, g.moneda) : '—'}</td>
                    </tr>
                    {abierto && (
                      <tr>
                        <td colSpan={6} className="px-4 py-3 bg-gray-50">
                          {cargandoDetalle ? (
                            <p className="text-sm text-gray-400">Cargando detalle...</p>
                          ) : (
                            <div className="space-y-2">
                              {detalle.map(r => (
                                <div key={r.id} className="flex items-center justify-between bg-white rounded-lg border border-gray-200 p-3 text-sm">
                                  <div>
                                    <p className="font-medium text-gray-800">{r.proveedor_nombre || 'Sin proveedor'} — {fmtMontoMoneda(r.monto, r.moneda)}</p>
                                    <p className="text-xs text-gray-500">{fmtFecha(r.fecha)} · comprobante {r.comprobante || '—'} · estado {r.estado}</p>
                                  </div>
                                  <div className="flex gap-2">
                                    {r.estado === 'emitida' && (
                                      <button
                                        onClick={() => handleDeclarar(r.id)}
                                        disabled={accionEnCurso === r.id}
                                        className="px-3 py-1.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-xs font-medium disabled:bg-gray-300"
                                      >
                                        Declarar
                                      </button>
                                    )}
                                    {r.estado === 'declarada' && !r.pagado_at && (
                                      <button
                                        onClick={() => handleMarcarPagada(r.id)}
                                        disabled={accionEnCurso === r.id}
                                        className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-xs font-medium disabled:bg-gray-300"
                                      >
                                        Marcar pagada
                                      </button>
                                    )}
                                    {r.pagado_at && <span className="text-xs text-emerald-600 font-medium">Pagada {fmtFecha(r.pagado_at)}</span>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {ModalRenderer}
    </div>
  );
};

const DIAS_SEMANA = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
// Fecha local en formato YYYY-MM-DD, sin pasar por toISOString() (que
// convierte a UTC y puede correr el día en Venezuela, UTC-4). Las fechas
// que devuelve el backend para columnas `date` ya vienen pre-ajustadas a
// esto mismo, por eso comparamos como texto, nunca como Date completo.
const fechaLocalISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dia}`;
};
const fmtDiaCorto = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  const fecha = new Date(y, m - 1, d);
  return `${DIAS_SEMANA[fecha.getDay()]} ${d}/${m}`;
};

const FlujoCajaTab = () => {
  const { notify, confirm, ModalRenderer } = useModal();
  const [saldos, setSaldos] = useState([]);
  const [resumen, setResumen] = useState([]);
  const [pendientes, setPendientes] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [mostrarForm, setMostrarForm] = useState(false);
  const [form, setForm] = useState({
    descripcion: '', fecha_programada: fechaLocalISO(new Date()),
    monto: '', moneda: 'USD', cuenta_bancaria_id: '', linea_negocio: ''
  });
  const [guardando, setGuardando] = useState(false);
  const [accionEnCurso, setAccionEnCurso] = useState(null);

  const cargarTodo = async () => {
    setCargando(true);
    setError('');
    try {
      const [resSaldos, resResumen, resPendientes, resCuentas] = await Promise.all([
        conciliacionBancariaService.getSaldos(),
        compromisosPagoService.getResumen(7),
        compromisosPagoService.getAll({ estado: 'pendiente' }),
        conciliacionBancariaService.getCuentas(true)
      ]);
      setSaldos(resSaldos.data || []);
      setResumen(resResumen.data || []);
      setPendientes(resPendientes.data || []);
      setCuentas(resCuentas.data || []);
    } catch (err) {
      setError(err.message || 'Error al cargar el flujo de caja');
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargarTodo(); }, []);

  const handleCrear = async () => {
    if (!form.descripcion || !form.fecha_programada || !form.monto) return;
    setGuardando(true);
    try {
      await compromisosPagoService.crear({
        ...form,
        cuenta_bancaria_id: form.cuenta_bancaria_id || null,
        linea_negocio: form.linea_negocio || null
      });
      setForm({ descripcion: '', fecha_programada: fechaLocalISO(new Date()), monto: '', moneda: 'USD', cuenta_bancaria_id: '', linea_negocio: '' });
      setMostrarForm(false);
      await cargarTodo();
    } catch (err) {
      await notify('error', 'Error', err.message || 'Error al crear el compromiso');
    } finally {
      setGuardando(false);
    }
  };

  const handleMarcarPagado = async (id) => {
    setAccionEnCurso(id);
    try {
      await compromisosPagoService.marcarPagado(id);
      await cargarTodo();
    } catch (err) {
      await notify('error', 'Error', err.message || 'Error al marcar como pagado');
    } finally {
      setAccionEnCurso(null);
    }
  };

  const handleCancelar = async (id) => {
    const ok = await confirm('Cancelar compromiso', '¿Cancelar este compromiso?');
    if (!ok) return;
    setAccionEnCurso(id);
    try {
      await compromisosPagoService.cancelar(id);
      await cargarTodo();
    } catch (err) {
      await notify('error', 'Error', err.message || 'Error al cancelar');
    } finally {
      setAccionEnCurso(null);
    }
  };

  if (cargando) return <div className="text-center py-12 text-gray-400">Cargando...</div>;

  const hoy = fechaLocalISO(new Date());
  const totalHoyPorMoneda = {};
  const totalSemanaPorMoneda = {};
  for (const r of resumen) {
    const fechaFila = r.fecha_programada.slice(0, 10);
    totalSemanaPorMoneda[r.moneda] = (totalSemanaPorMoneda[r.moneda] || 0) + Number(r.total);
    if (fechaFila === hoy) totalHoyPorMoneda[r.moneda] = (totalHoyPorMoneda[r.moneda] || 0) + Number(r.total);
  }

  const dias = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date();
    d.setDate(d.getDate() + i);
    const iso = fechaLocalISO(d);
    dias.push({ iso, delDia: resumen.filter(r => r.fecha_programada.slice(0, 10) === iso) });
  }

  return (
    <div className="space-y-6">
      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">{error}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.keys(totalHoyPorMoneda).length === 0 ? (
          <KpiCard icon={AlertCircle} label="A pagar hoy" value={fmtMoneda(0)} iconClass="text-red-600" />
        ) : Object.entries(totalHoyPorMoneda).map(([moneda, total]) => (
          <KpiCard key={moneda} icon={AlertCircle} label={`A pagar hoy (${moneda})`} value={fmtMontoMoneda(total, moneda)} iconClass="text-red-600" />
        ))}
        {Object.entries(totalSemanaPorMoneda).map(([moneda, total]) => (
          <KpiCard key={moneda} icon={Calendar} label={`A pagar próximos 7 días (${moneda})`} value={fmtMontoMoneda(total, moneda)} iconClass="text-amber-600" />
        ))}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
          <h3 className="font-semibold text-gray-700 text-sm">Saldo disponible por cuenta</h3>
          <p className="text-xs text-gray-500">Ancla al último "SALDO FINAL" real importado, más el neto de movimientos posteriores — no es un saldo en vivo.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4">
          {saldos.map(s => (
            <div key={s.cuenta_bancaria_id} className="border border-gray-200 rounded-lg p-3">
              <p className="text-sm font-medium text-gray-800">{s.alias}</p>
              {s.saldo === null ? (
                <p className="text-sm text-gray-400 italic mt-1">Sin extracto importado con saldo</p>
              ) : (
                <>
                  <p className="text-xl font-bold text-gray-800 mt-1">{fmtMontoMoneda(s.saldo, s.moneda)}</p>
                  <p className="text-xs text-gray-500 mt-1">Al {fmtFecha(s.fecha_saldo)}</p>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
          <h3 className="font-semibold text-gray-700 text-sm">Próximos 7 días</h3>
        </div>
        <div className="overflow-x-auto">
          <div className="flex divide-x divide-gray-100 min-w-full">
            {dias.map(d => (
              <div key={d.iso} className="flex-1 min-w-[110px] p-3">
                <p className="text-xs font-semibold text-gray-500 uppercase">{fmtDiaCorto(d.iso)}</p>
                {d.delDia.length === 0 ? (
                  <p className="text-xs text-gray-300 mt-2">—</p>
                ) : d.delDia.map(r => (
                  <p key={r.moneda} className="text-sm font-bold text-gray-800 mt-2">{fmtMontoMoneda(r.total, r.moneda)}</p>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
          <h3 className="font-semibold text-gray-700 text-sm">Compromisos de pago pendientes ({pendientes.length})</h3>
          <button onClick={() => setMostrarForm(!mostrarForm)} className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-medium">
            <Plus size={14} /> Nuevo compromiso
          </button>
        </div>

        {mostrarForm && (
          <div className="p-4 border-b border-gray-100 bg-gray-50 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input type="text" placeholder="Descripción" value={form.descripcion} onChange={e => setForm({ ...form, descripcion: e.target.value })} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              <input type="date" value={form.fecha_programada} onChange={e => setForm({ ...form, fecha_programada: e.target.value })} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <select value={form.moneda} onChange={e => setForm({ ...form, moneda: e.target.value })} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
                <option value="USD">USD</option>
                <option value="VES">VES</option>
              </select>
              <input type="number" step="0.01" placeholder="Monto" value={form.monto} onChange={e => setForm({ ...form, monto: e.target.value })} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              <select value={form.cuenta_bancaria_id} onChange={e => setForm({ ...form, cuenta_bancaria_id: e.target.value })} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
                <option value="">Cuenta (opcional)</option>
                {cuentas.map(c => <option key={c.id} value={c.id}>{c.alias}</option>)}
              </select>
              <select value={form.linea_negocio} onChange={e => setForm({ ...form, linea_negocio: e.target.value })} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
                <option value="">Línea (opcional)</option>
                {LINEAS_NEGOCIO.filter(l => l !== 'sin_linea').map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setMostrarForm(false)} className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
              <button onClick={handleCrear} disabled={guardando} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:bg-gray-300">
                {guardando ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        )}

        <div className="divide-y divide-gray-100">
          {pendientes.length === 0 ? (
            <p className="text-sm text-gray-400 italic p-4">Sin compromisos pendientes.</p>
          ) : pendientes.map(c => (
            <div key={c.id} className="flex items-center justify-between p-3">
              <div>
                <p className="text-sm font-medium text-gray-800">
                  {c.descripcion}
                  {c.linea_negocio && <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-semibold bg-purple-100 text-purple-700">{c.linea_negocio}</span>}
                </p>
                <p className="text-xs text-gray-500">{fmtFecha(c.fecha_programada)} · {fmtMontoMoneda(c.monto, c.moneda)} {c.cuenta_alias ? `· ${c.cuenta_alias}` : ''}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => handleMarcarPagado(c.id)} disabled={accionEnCurso === c.id} className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-xs font-medium disabled:bg-gray-300">
                  <CheckCircle2 size={12} /> Pagado
                </button>
                <button onClick={() => handleCancelar(c.id)} disabled={accionEnCurso === c.id} className="flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 text-xs font-medium">
                  <Ban size={12} /> Cancelar
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
      {ModalRenderer}
    </div>
  );
};

const PipelineTab = ({ pipeline }) => {
  const max = Math.max(1, ...pipeline.map(f => Number(f.cantidad) || 0));
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-4">
      {pipeline.length === 0 ? (
        <p className="text-center text-gray-400 py-8">Sin solicitudes registradas</p>
      ) : pipeline.map((f) => (
        <div key={f.fase} className="flex items-center gap-4">
          <div className="w-32 text-sm font-medium text-gray-700">{f.nombre}</div>
          <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
            <div
              className="bg-blue-500 h-6 rounded-full flex items-center justify-end px-2"
              style={{ width: `${(Number(f.cantidad) / max) * 100}%` }}
            />
          </div>
          <div className="w-10 text-right text-sm font-semibold text-gray-800">{f.cantidad}</div>
        </div>
      ))}
    </div>
  );
};

const VehiculosTab = ({ vehiculos }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200 text-left text-xs text-gray-500 uppercase">
            <th className="px-4 py-2">Placa</th>
            <th className="px-4 py-2">Nombre</th>
            <th className="px-4 py-2">Tipo</th>
            <th className="px-4 py-2 text-center">Solicitudes</th>
            <th className="px-4 py-2 text-right">Costo repuestos</th>
            <th className="px-4 py-2 text-right">Mano de obra</th>
          </tr>
        </thead>
        <tbody>
          {vehiculos.length === 0 ? (
            <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">Sin vehículos con solicitudes registradas</td></tr>
          ) : vehiculos.map((v) => (
            <tr key={v.id} className="border-b border-gray-100">
              <td className="px-4 py-2 font-bold text-amber-700 uppercase">{v.placa}</td>
              <td className="px-4 py-2">{v.nombre}</td>
              <td className="px-4 py-2 text-gray-500">{v.tipo}</td>
              <td className="px-4 py-2 text-center">{v.solicitudes}</td>
              <td className="px-4 py-2 text-right">{fmtMoneda(v.costo_repuestos)}</td>
              <td className="px-4 py-2 text-right">{fmtMoneda(v.mano_obra_facturada)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const LibroVentasTab = ({ filas }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200 text-left text-xs text-gray-500 uppercase">
            <th className="px-4 py-2">Fecha</th>
            <th className="px-4 py-2">N° Factura</th>
            <th className="px-4 py-2">RIF</th>
            <th className="px-4 py-2">Razón social</th>
            <th className="px-4 py-2 text-right">Base Bs</th>
            <th className="px-4 py-2 text-right">IVA Bs</th>
            <th className="px-4 py-2 text-right">Total Bs</th>
            <th className="px-4 py-2 text-right">IVA retenido</th>
            <th className="px-4 py-2">Estado</th>
          </tr>
        </thead>
        <tbody>
          {filas.length === 0 ? (
            <tr><td colSpan={9} className="px-4 py-6 text-center text-gray-400">Sin facturas en el rango seleccionado</td></tr>
          ) : filas.map((f, i) => (
            <tr key={i} className="border-b border-gray-100">
              <td className="px-4 py-2 text-gray-500">{fmtFecha(f.fecha)}</td>
              <td className="px-4 py-2 font-medium">{f.numero_factura}</td>
              <td className="px-4 py-2">{f.rif || '—'}</td>
              <td className="px-4 py-2">{f.razon_social}</td>
              <td className="px-4 py-2 text-right">{formatearBolivares(f.base_imponible_ves)}</td>
              <td className="px-4 py-2 text-right">{formatearBolivares(f.iva_ves)}</td>
              <td className="px-4 py-2 text-right font-semibold">{formatearBolivares(f.total_ves)}</td>
              <td className="px-4 py-2 text-right">{formatearBolivares(f.iva_retenido)}</td>
              <td className="px-4 py-2 text-gray-500">{f.estado}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const LibroComprasTab = ({ filas }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200 text-left text-xs text-gray-500 uppercase">
            <th className="px-4 py-2">Fecha</th>
            <th className="px-4 py-2">N° Proveedor</th>
            <th className="px-4 py-2">RIF</th>
            <th className="px-4 py-2">Razón social</th>
            <th className="px-4 py-2 text-right">Base Bs</th>
            <th className="px-4 py-2 text-right">IVA Bs</th>
            <th className="px-4 py-2 text-right">Total Bs</th>
            <th className="px-4 py-2 text-right">IVA retenido</th>
            <th className="px-4 py-2 text-right">ISLR retenido</th>
            <th className="px-4 py-2">Estado</th>
          </tr>
        </thead>
        <tbody>
          {filas.length === 0 ? (
            <tr><td colSpan={10} className="px-4 py-6 text-center text-gray-400">Sin facturas de compra en el rango seleccionado</td></tr>
          ) : filas.map((f, i) => (
            <tr key={i} className="border-b border-gray-100">
              <td className="px-4 py-2 text-gray-500">{fmtFecha(f.fecha)}</td>
              <td className="px-4 py-2 font-medium">{f.numero_proveedor}</td>
              <td className="px-4 py-2">{f.rif || '—'}</td>
              <td className="px-4 py-2">{f.razon_social}</td>
              <td className="px-4 py-2 text-right">{formatearBolivares(f.base_imponible_ves)}</td>
              <td className="px-4 py-2 text-right">{formatearBolivares(f.iva_ves)}</td>
              <td className="px-4 py-2 text-right font-semibold">{formatearBolivares(f.total_ves)}</td>
              <td className="px-4 py-2 text-right">{formatearBolivares(f.iva_retenido)}</td>
              <td className="px-4 py-2 text-right">{formatearBolivares(f.islr_retenido)}</td>
              <td className="px-4 py-2 text-gray-500">{f.estado}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export default ReportesView;
